/**
 * 사용자 설정 타입 및 상수 정의
 * 폰트 모달(Layout) / 설정 패널(SettingsPanel) 역할 분리
 */

// ============================================================================
// 타입 정의
// ============================================================================

export interface BookViewerSettings {
  fontSize: number; // 글자 크기 (px)
  fontFamily: string; // 폰트 패밀리
  lineHeight: number; // 줄 간격
  letterSpacing: number; // 자간
  pagesPerView: 1 | 2; // 한 화면에 보이는 페이지 수
}

export interface FontSettings {
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  letterSpacing: number;
  lineHeight: number;
}

// ============================================================================
// 기본값
// ============================================================================

export const DEFAULT_SETTINGS: BookViewerSettings = {
  fontSize: 16,
  fontFamily: 'inherit',
  lineHeight: 1.8,
  letterSpacing: 0,
  pagesPerView: 1,
};

export const DEFAULT_FONT_SETTINGS: FontSettings = {
  fontFamily: 'inherit',
  fontSize: 16,
  fontWeight: 400,
  letterSpacing: 5,
  lineHeight: 1.8,
};

// ============================================================================
// 폰트 모달용 (Layout - 책뷰어 폰트 설정)
// ============================================================================

export const FONT_MODAL_RANGES = {
  fontSize: { min: 10, max: 22 },
  fontWeight: { min: 0, max: 8 },
  letterSpacing: { min: 0, max: 20 },
  lineHeight: { min: 0, max: 20 },
} as const;

/** 글자크기, 굵기, 자간, 행간 모두 5칸 스냅 - 초기값: 크기 3번째, 굵기 2번째, 자간 2번째, 행간 3번째 */
export const FONT_MODAL_SNAP_POINTS = {
  fontSize: [10, 13, 16, 19, 22] as const,
  fontWeight: [0, 2, 4, 6, 8] as const,
  letterSpacing: [0, 5, 10, 15, 20] as const,
  lineHeight: [0, 5, 10, 15, 20] as const,
} as const;

// 행간 변환 (0~20 ↔ 1.3~2.1, 3번째 칸=10에 1.8 매핑)
export const lineHeightToValue = (lineHeight: number): number => {
  if (lineHeight <= 1.8) return ((lineHeight - 1.3) / 0.5) * 10;
  return 10 + ((lineHeight - 1.8) / 0.3) * 10;
};

export const valueToLineHeight = (value: number): number => {
  if (value <= 10) return 1.3 + (value / 10) * 0.5;
  return 1.8 + ((value - 10) / 10) * 0.3;
};

// 굵기 변환 (0~8 ↔ 300~700)
export const fontWeightToValue = (fontWeight: number): number => {
  const min = 300;
  const max = 700;
  if (fontWeight < min) return 0;
  if (fontWeight > max) return 8;
  return Math.round(((fontWeight - min) / (max - min)) * 8);
};

export const valueToFontWeight = (value: number): number => {
  const clampedValue = Math.max(0, Math.min(8, value));
  return Math.round(300 + (clampedValue / 8) * 400);
};

// ============================================================================
// 설정 패널용 (SettingsPanel - 읽기 설정)
// ============================================================================

export const SETTINGS_PANEL_RANGES = {
  lineHeight: { min: 1.0, max: 2.5 },
  letterSpacing: { min: -2, max: 4 },
} as const;

/** 줄간격, 자간 각 5칸 스냅 */
export const SETTINGS_PANEL_SNAP_POINTS = {
  lineHeight: [1.0, 1.375, 1.75, 2.125, 2.5] as const,
  letterSpacing: [-2, -0.5, 1, 2.5, 4] as const,
} as const;

export const findNearestSnapPoint = (value: number, snapPoints: readonly number[]): number =>
  snapPoints.reduce((prev, curr) =>
    Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
  );

// ============================================================================
// 폰트 옵션
// ============================================================================

export const FONT_OPTIONS = [
  { value: 'inherit', label: '기본 폰트' },
  { value: 'Noto Sans KR', label: 'Noto Sans KR' },
  { value: 'Nanum Gothic', label: '나눔고딕' },
  { value: 'Nanum Myeongjo', label: '나눔명조' },
  { value: 'Malgun Gothic', label: '맑은 고딕' },
  { value: 'Gulim', label: '굴림' },
  { value: 'Dotum', label: '돋움' },
  { value: 'Batang', label: '바탕' },
  { value: 'Gungsuh', label: '궁서' },
];

export const FONT_SIZE_OPTIONS = [
  { value: 12, label: '12px' },
  { value: 14, label: '14px' },
  { value: 16, label: '16px' },
  { value: 18, label: '18px' },
  { value: 20, label: '20px' },
  { value: 22, label: '22px' },
  { value: 24, label: '24px' },
];
